import { forwardRef, useId, useLayoutEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import asqioLogo from "../../assets/asqio_logo_nav.webp";
import { AiIcon } from "../../assets/icons/aiIcon";
import { designSystemVars as vars } from "../tokens";
import { IconButton, IconButtonProps } from "./Button";

export type PanelVisibility = "open" | "minimized";

const appear = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) translateY(8px) scale(0.92); }
  to { opacity: 1; transform: translate(-50%, -50%); }
`;

const sparkle = keyframes`
  0%, 16%, 100% {
    opacity: 0.65;
    fill-opacity: 0;
    transform: scale(1) rotate(0deg);
  }
  4%, 11% {
    opacity: 1;
    fill-opacity: 0.75;
    transform: scale(1.6) rotate(12deg);
  }
  7% {
    opacity: 0.4;
    fill-opacity: 0;
    transform: scale(0.9) rotate(-6deg);
  }
`;

const LauncherSparkles = styled.g`
  transform-box: fill-box;
  transform-origin: center;
  fill-opacity: 0;
  animation: ${sparkle} ${vars.motion.attentionInterval} ${vars.motion.easing}
    infinite;

  svg path {
    fill: currentColor;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  button:disabled & {
    animation: none;
  }
`;

const LauncherAnchor = styled.span<{ $scale: number }>`
  position: fixed;
  right: max(
    8vmax,
    calc(
      (${vars.spacing.px60} * 0.65 + ${vars.spacing.sm}) *
        ${({ $scale }) => $scale}
    )
  );
  bottom: max(
    8vmax,
    calc(
      (${vars.spacing.px60} * 0.65 + ${vars.spacing.sm}) *
        ${({ $scale }) => $scale}
    )
  );
  z-index: ${vars.zIndex.toast};
  width: 0;
  height: 0;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: 0 0;
`;

const Launcher = styled(IconButton)`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -50%);
  width: calc(${vars.spacing.px60} * 1.3);
  min-width: calc(${vars.spacing.px60} * 1.3);
  height: calc(${vars.spacing.px60} * 1.3);
  overflow: visible;
  padding: ${vars.spacing.xs};
  border: ${vars.borderWidth.thin} solid ${vars.color.action.primary};
  border-radius: ${vars.radius.circle};
  background: ${vars.color.action.primary};
  box-shadow: none;
  animation: ${appear} ${vars.motion.slow} ${vars.motion.easing};

  &:focus-visible {
    box-shadow: ${vars.shadow.focus};
  }

  &:not(:disabled):hover,
  &:not(:disabled):active {
    background: ${vars.color.action.primary};
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${vars.color.text.inverse};
    opacity: 0;
    pointer-events: none;
    transition: opacity ${vars.motion.fast} ${vars.motion.easing};
  }

  &:not(:disabled):hover::after,
  &:not(:disabled):active::after {
    opacity: 0.12;
  }

  img {
    width: calc(${vars.spacing.px48} * 1.3);
    height: calc(${vars.spacing.px48} * 1.3);
    object-fit: contain;
    filter: invert(1);
    mix-blend-mode: screen;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
    &::after {
      transition: none;
    }
  }
`;

const useLauncherScale = () => {
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    let reference: { display: string; pixelRatio: number } | null = null;
    const updateScale = () => {
      const pixelRatio = window.devicePixelRatio || 1;
      // Quantize screen dimensions to tolerate rounding at fractional zoom levels.
      const display = `${Math.round((window.screen.width * pixelRatio) / 32)}x${Math.round((window.screen.height * pixelRatio) / 32)}`;

      if (reference?.display !== display) {
        const viewportRatio =
          window.outerWidth && window.innerWidth
            ? window.innerWidth / window.outerWidth
            : 1;
        let referenceRatio = pixelRatio * viewportRatio;
        try {
          const key = `asqio-launcher-display-ratio:${display}`;
          const saved = Number(window.sessionStorage.getItem(key));
          if (Number.isFinite(saved) && saved > 0) referenceRatio = saved;
          window.sessionStorage.setItem(key, String(referenceRatio));
        } catch {
          // Keep the in-memory reference when browser storage is unavailable.
        }
        reference = { display, pixelRatio: referenceRatio };
      }

      setScale(reference.pixelRatio / pixelRatio);
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  return scale;
};

const LauncherLabel = styled.svg`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(
    ${vars.spacing.px60} * 1.3 + ${vars.spacing["2xl"]} -
      ${vars.borderWidth.thin} * 2
  );
  height: calc(
    ${vars.spacing.px60} * 1.3 + ${vars.spacing["2xl"]} -
      ${vars.borderWidth.thin} * 2
  );
  overflow: visible;
  pointer-events: none;
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.xs};
  font-weight: ${vars.typography.weight.semibold};
  letter-spacing: normal;
`;

const LauncherArtwork = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${vars.radius.circle};
`;

export const FloatingPanelLauncher = forwardRef<
  HTMLButtonElement,
  IconButtonProps
>((props, ref) => {
  const arcId = `asqio-launcher-${useId().replace(/:/g, "")}`;
  const scale = useLauncherScale();
  return (
    <LauncherAnchor $scale={scale}>
      <Launcher {...props} ref={ref}>
        <LauncherLabel
          viewBox="0 0 110 110"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <path id={arcId} d="M 10 55 A 45 45 0 0 1 100 55" />
          </defs>
          <g transform="translate(25 12) rotate(-35)">
            <LauncherSparkles>
              <g transform="translate(-7 -7)">
                <AiIcon size={14} strokeColor="currentColor" />
              </g>
            </LauncherSparkles>
          </g>
          <text fill="currentColor" textAnchor="middle">
            <textPath href={`#${arcId}`} startOffset="56%">
              Asqio AI
            </textPath>
          </text>
        </LauncherLabel>
        <LauncherArtwork>
          <img src={asqioLogo} alt="" />
        </LauncherArtwork>
      </Launcher>
    </LauncherAnchor>
  );
});
FloatingPanelLauncher.displayName = "FloatingPanelLauncher";
